import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  CircularProgress, 
  Button, 
  TextField, 
  Paper, 
  Alert
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import { apiService } from '../utils/api';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});

  // トークンの検証
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('リセットトークンがありません');
        setLoading(false);
        return;
      }

      try {
        // トークン検証APIエンドポイントを呼び出す
        const response = await apiService.post('/api/auth/validate-reset-token', { token });
        
        if (response.data && response.data.valid) {
          setValidToken(true);
        } else {
          setError('パスワードリセットトークンが無効または期限切れです');
        }
      } catch (err) {
        
        setError(err.response?.data?.message || 'トークン検証中にエラーが発生しました');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  // フォームの検証
  const validateForm = () => {
    const errors = {};

    if (!password) {
      errors.password = '新しいパスワードを入力してください';
    } else if (password.length < 8) {
      errors.password = 'パスワードは8文字以上である必要があります';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'パスワードを再入力してください';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'パスワードが一致しません';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // パスワードリセットの送信
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // フォームの検証
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // パスワードリセットAPIエンドポイントを呼び出す
      const response = await apiService.post('/api/auth/reset-password', {
        token,
        password,
        password_confirmation: confirmPassword
      });

      if (response.data && response.data.success) {
        setSuccess(true);
        setError('');
        // フォームをクリア
        setPassword('');
        setConfirmPassword('');
      } else {
        setError('パスワードのリセットに失敗しました');
      }
    } catch (err) {
      
      setError(err.response?.data?.message || 'パスワードリセット中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="80vh">
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            リクエストを処理中...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 6, textAlign: 'center' }}>
          <LockResetIcon color="primary" sx={{ fontSize: 60 }} />
          <Typography variant="h5" sx={{ mt: 2 }}>
            パスワードを変更しました
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            新しいパスワードでログインしてください。
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            fullWidth 
            sx={{ mt: 3 }}
            onClick={handleGoToLogin}
          >
            ログインページへ
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 6 }}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <LockResetIcon color="primary" sx={{ fontSize: 60 }} />
          <Typography variant="h5" sx={{ mt: 2 }}>
            パスワードをリセット
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}
          
          {!validToken ? (
            <Box sx={{ mt: 2, width: '100%', textAlign: 'center' }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                パスワードリセットリンクが無効または期限切れです。
              </Typography>
              <Button 
                variant="contained" 
                onClick={handleGoToLogin}
              >
                ログインページへ
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="新しいパスワード"
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!formErrors.password}
                helperText={formErrors.password}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="パスワードを再入力"
                type="password"
                id="confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={!!formErrors.confirmPassword}
                helperText={formErrors.confirmPassword}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'パスワードを変更'}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default ResetPassword; 